import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getPlaylist } from '../../api/client';
import type { PlaylistApi, TrackApi } from '../../api/client';
import type { Playlist, Track } from '../home/page';
import PlaylistSection from '../home/components/PlaylistSection';
import { useAuth } from '../../context/AuthContext';

function mapApiTrack(t: TrackApi): Track {
  return {
    id: t.id,
    title: t.title,
    artist: t.artist,
    album: t.album,
    duration: t.duration,
    image: t.image || '',
    audioUrl: t.audio_url || undefined,
    isLiked: !!t.is_liked,
  };
}

function mapApiPlaylist(p: PlaylistApi): Playlist {
  const rawCover = p.cover_image ?? p.coverImage ?? '';
  const coverImage = typeof rawCover === 'string' && rawCover.trim().length > 0 ? rawCover.trim() : '';
  return {
    id: p.id,
    name: p.name,
    description: p.description || '',
    tracks: (p.tracks || []).map(mapApiTrack),
    coverImage,
    createdAt: p.created_at ?? p.createdAt ?? '',
    visibility: p.visibility ?? 'public',
    isLiked: !!p.is_liked,
    likesCount: p.likes_count ?? 0,
    commentsCount: p.comments_count ?? 0,
    ownerUsername: p.owner_username ?? null,
    ownerAvatarUrl: p.owner_avatar_url ?? null,
  };
}

/**
 * View a playlist by URL (e.g. from a user profile). Listen and comment here,
 * or open in the Listening room on the home page.
 */
export default function PublicPlaylistPage() {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [playlist, setPlaylist] = useState<Playlist | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const id = idParam ? parseInt(idParam, 10) : NaN;

  useEffect(() => {
    if (isNaN(id)) {
      setError('Invalid playlist');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    getPlaylist(id)
      .then((p) => {
        if (!cancelled) setPlaylist(mapApiPlaylist(p));
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Playlist not found');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const goListen = (track?: Track) => {
    const q = new URLSearchParams();
    q.set('playlist', String(id));
    if (track) q.set('track', String(track.id));
    navigate(`/?${q.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#fcfbf7_0%,#f6efe2_45%,#f5f7f2_100%)] pt-14 text-stone-900">
      <header className="sticky top-14 z-40 border-b border-stone-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-stone-100 text-stone-700 hover:bg-stone-200"
            aria-label="Back"
          >
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-bold text-stone-900" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
              {playlist?.name || 'Playlist'}
            </h1>
            {playlist?.ownerUsername ? (
              <p className="truncate text-xs text-stone-500">
                <Link to={`/u/${encodeURIComponent(playlist.ownerUsername)}`} className="text-amber-800 hover:underline">
                  @{playlist.ownerUsername}
                </Link>
              </p>
            ) : null}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        {loading ? (
          <p className="text-center text-stone-500">Loading playlist…</p>
        ) : error || !playlist ? (
          <div className="rounded-2xl border border-stone-200 bg-white/80 p-8 text-center">
            <p className="text-stone-700">{error || 'Playlist not found.'}</p>
            <p className="mt-2 text-sm text-stone-500">
              {isLoggedIn
                ? 'It may be private or only visible to followers.'
                : 'Sign in to see playlists shared with followers only.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-4 rounded-xl bg-[#6B3B18] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95"
            >
              Go home
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">Listen</p>
                <p className="mt-1 text-sm text-stone-600">
                  Play in the full listening room with the bottom player, or tap a track to jump there.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => goListen()}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#6B3B18] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95"
                >
                  <i className="ri-home-4-line" aria-hidden />
                  Open in listening room
                </button>
                {playlist.ownerUsername ? (
                  <Link
                    to={`/u/${encodeURIComponent(playlist.ownerUsername)}`}
                    className="inline-flex items-center gap-2 rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-sm font-medium text-stone-800 hover:bg-stone-50"
                  >
                    <i className="ri-user-line" aria-hidden />
                    View profile
                  </Link>
                ) : null}
              </div>
            </div>

            <PlaylistSection
              currentTrack={{
                id: 0,
                title: '',
                artist: '',
                album: '',
                duration: '',
                image: '',
              }}
              setCurrentTrack={() => {}}
              playlist={playlist}
              onRemoveTrack={() => {}}
              canRemoveTracks={false}
              onTrackSelect={(track) => goListen(track)}
            />
          </div>
        )}
      </main>
    </div>
  );
}
